import { Field, Form, Formik, useFormikContext } from 'formik'
import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, Progress, Box, Text, VStack, useToast, Alert, AlertIcon, Spinner, Flex, UnorderedList } from '@chakra-ui/react'
import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { Actions, OptionButton, OptionButtonList, OptionsHeader, Question, SubmitButton } from '../../styles/PathStyles'
import { BottomExplainer, TermsLink } from '../Auth/Signup/SignupModal2Styles'
import { isPossiblePhoneNumber , Value} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useMutation, useQuery } from 'react-query'
import { AuthModal } from '../Auth/withAuthModal'
import { auth, getToken } from "../../src/config/firebase";
import { clientAnalytics, clientError, env_check, generatePass } from '../../utils/utilties'
import { useAuth } from '../../utils/auth/newAuth';
import dynamic from 'next/dynamic'
import { createUserWithEmailAndPassword, deleteUser, updateProfile } from 'firebase/auth'

interface Props {
    isOpen: boolean
    onClose: () => void
    listingData: {
        id: number,
    }
    onMessageSent: (convoId: number) => void
    quizType: string,
    messageText: string,
}

const PhoneNumberInput = dynamic(() => import('../../components/PhoneNumberInput/PhoneNumberInput'))

function QuestionsLeadForm(props: Props) {
    const {
        listingData: {
            id: listingId,
        },
        quizType,
        messageText
    } = props
    const initialStep = 1
        
    const formSchema = quizType == "question" ? {
        fullName: Yup.string()
          .required("Please enter your full name")
          .min(2, "Full name must be at least 2 characters")
          .max(50, "Full name must be less than 100 characters"),
        email: Yup.string().required("Email is required"),
        message: Yup.string().max(1000, "Too long.").min(1, "Too short.")
    } : {
        message: Yup.string().max(1000, "Too long.").min(1, "Too short.")
    }


    const initialValues = quizType == "question" ? {
        message: "",
        email: "",
        fullName: ""
    } : {
        message: ""
    }

    const { user, profile, userId } = useAuth()
    const [userType, setUserType] = useState<'existing' | 'new'>()
    const [showAuthModal, setShowAuthModal] = useState(false)
    const [phoneInvalidError, setPhoneInvalidError] = useState(null)

    const openAuthModal = () => {
        setShowAuthModal(true);
    }
    const closeAuthModal = () => {
        setShowAuthModal(false);
    }

    const submitStatuses = ['sending_message', 'error', 'loading', 'idle'] as const;
    const [formValues, setFormValues] = useState<typeof initialValues>(initialValues)
    const [convoId, setConvoId] = useState(null)
    const [phoneInputValue, setPhoneInputValue] = useState("" as Value)
    const [submitStatus, setSubmitStatus] = useState<typeof submitStatuses[number]>("idle")
    const [alert, setAlert] = useState<{ status: 'error' | 'success', description: string }>(null)
    const [accountExists, setAccountExists] = useState<boolean>(false)

    const toast = useToast()
    const [step, setStep] = useState(initialStep)
    
    const trackAnalytics = (eventName: string, options?: Record<any, any>) => {
        if (env_check) {
            clientAnalytics.track(eventName, options)
        }
    }

    const onMessageSent = async (convoId: number) => {
        setConvoId(convoId)
        props.onMessageSent(convoId)

        trackAnalytics(`${quizType == "question" ? "Ask a question" : "Request callback"} - flow complete`, {
          ...props.listingData
        });

        setSubmitStatus('idle')
    }

    useEffect(() => {
        if (!!profile && userType === 'existing') {
            messageMutation.mutate({
                uid: userId,
                message: formValues.message,
                listingId,
            })
        }
    }, [profile])

    const commonTimings = [
        "Tomorrow",
        "Anytime",
        "Evenings",
        "This weekend",
        "During working hours",
    ]
    const handlePhoneValueChange = (phone) => {
        setPhoneInputValue( !!phone ? phone : "" )
    }

    function callBackRequest(values: typeof formValues) {
        // Create a message 
        messageMutation.mutate({
            uid: userId,
            message: `Could you please give me a call about this? ${values.message} would be good`,
            listingId,
        })
        // If phone number shared, add to user's profile
        if (!profile?.phone || profile?.phone?.length < 10) {
            updateMutation.mutate({
                phone: phoneInputValue
            })
        }

    }

    function confirmPhoneAndMessage(values: typeof formValues) {

        // Create a message 
        messageMutation.mutate({
            uid: userId,
            message: messageText,
            listingId,
        })

        // If phone number shared, add to user's profile
        if (!profile?.phone || profile?.phone?.length < 10) {
            updateMutation.mutate({
                phone: phoneInputValue
            })
        }

    }

    const updateUserRequest = async (passedData) => {
        // setDeleteProcessLoading(true);
        const response = await fetch(`/api/users/${userId}/update`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: await getToken(),
          },
          body: JSON.stringify({ updatedData: passedData }),
        });
    
        const data = await response.json();
        const { user } = data;
    
        return user;
      };
    
      const updateMutation = useMutation(updateUserRequest, {
        onError: (error) => {
          clientError(error);
        },
      });
    

    interface FormDataForSteps {
        header: string,
        explainer: string
        name: string,
        // inputComponent: React.ReactChild,
        // otherComponents?: React.ReactChild[]
        inputComponent: any,
        otherComponents?: any[],
        afterSubmit?: (values?: typeof formValues) => any
    }

    const formDataForSteps: Record<number, FormDataForSteps> = 
        quizType == "question" ?
    {
        1: {
            header: 'Ask a question to the listing owner',
            explainer: "Example: 'How old is the property?', 'Are you flexible on price', etc. or anything else you'd like",
            name: 'message',
            inputComponent: MessageInput,
            afterSubmit: function() {
                trackAnalytics("Ask a question - Question completed");
                nextStep()
            }
        },
        2: {
            header: "Enter your email where you'd like to get the response",
            explainer: "",
            name: 'email',
            inputComponent: EmailInput,
            afterSubmit: validateUserEmail,
        },
        3: {
            header: "What's your full name?",
            name: 'fullName',
            explainer: "Sharing your full name adds credibility to your question",
            inputComponent: FullNameInput,
            afterSubmit: function() {
                trackAnalytics("Ask a question - Name completed");
                nextStep()
            }
        },
        4: {
            header: "Verify your phone number to be notified of answers",
            explainer: "This helps us ensure that users are not robots",
            name: "phone",
            inputComponent: PhoneNumberInput,
            afterSubmit: validateUserPhone
        }
    } : quizType == "callback" ?
        profile?.phone && profile?.phone.length >= 10 ?
        {
            1: {
                header: 'When do you want to receive a call?',
                explainer: "Example: Evenings, weekends, after 5pm, anytime",
                name: 'message',
                inputComponent: MessageInput,
                afterSubmit: callBackRequest
            }
        } : {
            1: {
                header: "What's your number?",
                explainer: "This information is kept secure and not shared without your consent.",
                name: "phone",
                inputComponent: PhoneNumberInput,
                afterSubmit: function() {
                    trackAnalytics("Request call back - Question completed");
                    runValidatePhoneQuery();
                }
            },
            2: {
                header: 'When do you want to receive a call?',
                explainer: "Example: Evenings, weekends, after 5pm, anytime",
                name: 'message',
                inputComponent: MessageInput,
                afterSubmit: callBackRequest
            }
        } :
        quizType == "confirmPhone" ?
        {
            1: {
                header: "Confirm your number to send a message",
                explainer: "This step is important to ensure that you are a real person.",
                name: "phone",
                inputComponent: PhoneNumberInput,
                afterSubmit: confirmPhoneAndMessage

            },
          
        } :
        null;

    function nextStep() {
        if (step === Object.keys(formDataForSteps).length) return
        setStep(step + 1)
    }
    function prevStep() {
        setStep(step - 1)
    }

    async function findUserByEmail(email: typeof formValues['email']) {
        setSubmitStatus('loading')
        try {
            const response = await fetch(`/api/users?checkEmailExists=true`, {
                method: "GET",
                headers: { email },
            });
            const data = await response.json();
            if (response.ok) {
                if (data['userExists']) {
                    setAccountExists(true)
                    setUserType('existing')

                    trackAnalytics("Ask a question - Existing account detected");
                }
                else {
                    const {
                        validity: {
                          response: { result, status }
                        }
                      } = data;
                      if (
                        status != "success" ||
                        result == "valid" ||
                        result == "unknown" ||
                        result == "catchall"
                      ) {
                        // Email is valid
                        // trackAnalytics("Valid email, signup form displayed");
                        setUserType('new')
                        nextStep()
                      }
                      else {
                        setAlert({
                            status: 'error',
                            description: `Hmm, that email doesn't look right`
                        })
                      }
                }
            }
        } catch (error) {
            toast({
                status: 'error',
                description: 'An unexpected error occured'
            })
        }
        finally {
            setSubmitStatus('idle')
        }
    }

    const validatePhoneQuery = async () => {
        setSubmitStatus('loading')
        const response = await fetch(
          `/api/validatePhoneNumber?phone=${phoneInputValue}`,
          {
            method: "GET",
          }
        );
        const data = await response.json();
        const { result } = data;
    
        return result;
    };
    
    const {
        refetch: runValidatePhoneQuery,
    } = useQuery("validateNumber in QuestionsLeadForm", validatePhoneQuery, {
        enabled: false,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
          if (!data || data != "No exist") {

            // If quiz type is question - this leads to the account being created, in request callback, this leads to the next step
            if (quizType == 'callback') {
                nextStep();
                setSubmitStatus('idle')
                return;
            }

            //   Sign up user
            createUserAccount({
                email: formValues.email,
                fullName: formValues.fullName,
                phone: phoneInputValue
            })
          } else {
            trackAnalytics("Ask a question - Phone invalid")
            setSubmitStatus('error');
            setAlert({
                status: 'error',
                description: 'Phone number is invalid'
            })
          }
        },
        onError: (e) => {
          console.error(e);
          setSubmitStatus("error");
          toast({
            title: "Error",
            description: `An error occured trying to send you a verification code. Please try again later.`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
    });

    async function findUserByPhone()  {
        setSubmitStatus('loading')
        try {
            const response = await fetch(`/api/users?checkPhoneExists=true`, {
                method: "GET",
                headers: { phone: phoneInputValue },
            });
            const data = await response.json();
            if (data['userExists']) {
                setAccountExists(true)
                setUserType('existing')
                setSubmitStatus('idle')

                trackAnalytics("Ask a question - Existing account detected");
            }
            else {
                setUserType('new')
                runValidatePhoneQuery()
            }
        } catch (error) {
            setSubmitStatus('idle')
            toast({
                status: 'error',
                description: 'An unexpected error occured'
            })
        }
    }

    async function createMessageRequest(messageData) {
        setSubmitStatus('sending_message')
        const response = await fetch("/api/messages/create?scenario=L1&message_type=QuestionMessage", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: await getToken(),
          },
          body: JSON.stringify({ message: messageData }),
        });
    
        const data = await response.json();
        const {
          theMessage: { convoId },
        } = data;
        return convoId;
      }
    
      const messageMutation = useMutation(createMessageRequest, {
        onError: (error) => {
          // An error happened!
          clientError(error);
            setSubmitStatus('error')
          toast({
            position: "bottom",
            title: "Message failed",
            description: `Your message failed to send to ${formValues.fullName}. We're looking into this now and will get back to you as soon as we can.`,
            status: "error",
            variant: "subtle",
            duration: 5000,
          });
        },
    
        onSuccess: async (data, variables, context) => {
          if (data) {
            onMessageSent(data)
          } else {
            throw "An error occured trying to send DM";
          }
        },
      });

    async function createUserRequest(userData) {
        let IPAddress;
        try {
          const IPresponse = await fetch("https://jsonip.com", { mode: "cors" });
          const IPobj = await IPresponse.json();
          IPAddress = IPobj ? IPobj["ip"] : null;
        } catch (e) {
          clientError(e);
        }
    
        const response = await fetch("/api/users/create", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: await getToken(),
            ip: IPAddress ? IPAddress : null,
          },
          body: JSON.stringify({ user: userData }),
        });
    
        const data = await response.json();
        const { user } = data;
    
        return user;
      }
    
    const createUserRequestMutation = useMutation(createUserRequest, {
    onError: (error) => {
        toast({
            title: "Error",
            description: `An error occured. Please try again later.`,
            status: "error",
            duration: 5000,
            isClosable: true,
        });
        clientError(error);
        // setSignupStatus("error");
    //   setErrorMessage(error["message"]);
    },

    onSuccess: async (data) => {
        if (data) {
            const { uid } = data;
            messageMutation.mutate({
                uid,
                message: formValues.message,
                listingId,
            });
        }

        // User record didn't go through
        else {
        // Throw error
        // Delete user record from firebase
        await deleteUser(auth.currentUser)

        throw "Error creating user after Google auth";

        // Set method back to blank
        // setMethod("");
        }
    },
    });

    const createUserAccount = async (userData: any) => {
        const {
            email,
            fullName,
            phone,
        } = userData;
        try {
            let pass = generatePass();
            const response = await createUserWithEmailAndPassword(auth, email, pass);
            // If user is on the sign up page when signing up
            if (response.user) {
                const { uid } = response.user;

                trackAnalytics("User created", {
                    type: "Email",
                    uid: uid,
                });

                clientAnalytics.identify(uid, {
                    name: fullName,
                    email: email,
                    phone: phone,
                });

                const [firstName, lastName] = fullName.split(" ")
                // Don't send password up to the server
                const userData = {
                    email,
                    firstName,
                    lastName,
                    fullName,
                    phone,
                    uid,
                };

                await updateProfile(auth.currentUser, {
                    displayName: fullName
                })

                createUserRequestMutation.mutate(userData);
            } else {
                toast({
                    title: "Error",
                    description: `An error occured. Please try again later.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                clientError(
                    "Firebase did not come back with user in quiz funnel"
                );
            }
        } catch (error) {
            clientError(error);
            toast({
            title: "Error",
            description: error?.message
                ? error.message
                : "An error occured, please try again later",
            status: "error",
            duration: 5000,
            isClosable: true,
            });
        }
    };

    function validateUserEmail(values: typeof formValues) {
        trackAnalytics("Ask a question - Email completed")
        accountExists && setAccountExists(false)
        findUserByEmail(values.email)
    }
 

    const currentStepData = formDataForSteps[step]
    const currentSchema = formSchema[currentStepData.name]

    const phoneIsInvalid = useMemo(() => {
        return (
            (currentStepData.name === 'phone' && !isPossiblePhoneNumber(phoneInputValue)) ||
            (currentStepData.name === 'phone' && !phoneInputValue)
        )
    }, [phoneInputValue])

    function validateUserPhone() {
        // if (phoneIsInvalid) {
        //     setPhoneInvalidError("Phone is invalid")
        //     return;
        // }
        // !!setPhoneInputValue && setPhoneInvalidError(null)
        trackAnalytics("Ask a question - Phone completed")
        findUserByPhone()
    }

    const handleSubmit = (values) => {
        setFormValues(values)
        !!alert && setAlert(null)
        if (currentStepData.afterSubmit) {
            currentStepData.afterSubmit(values)
            return;
        }
        nextStep()
    }

 
    return (
        (<Modal
            isCentered
            isOpen={props.isOpen}
            // isOpen={true}
            onClose={props.onClose}
            closeOnOverlayClick
        >
            <ModalOverlay />
            <ModalContent>
                <Progress
                    hasStripe
                    colorScheme="green"
                    size="sm"
                    height="4px"
                    value={
                        ( step / Object.keys(formDataForSteps).length ) * 100
                    }
                />
                <ModalCloseButton />
                <ModalBody>
                    {
                        submitStatus === 'sending_message' ?
                        (
                            <Flex 
                                m="40px 0 20px 0"
                                justifyContent="center"
                                rowGap="35px"
                                alignItems="center"
                                flexDir="column"
                            >
                                <Question>
                                    {quizType == "question" ? "Sending question" : 
                                        quizType == "callback" ?
                                    "Requesting call back"
                                : 
                                    quizType == "confirmPhone" ?
                                    "Sending message" : null
                                }
                                </Question>
                                <Spinner size="lg" color="green.800" />
                            </Flex>
                        ) :
                        (
                        <Formik
                            initialValues={initialValues}
                            onSubmit={(values, { setSubmitting }) => {
                                handleSubmit(values)
                            }}
                            validationSchema={Yup.object().shape({
                                [currentStepData.name]: currentSchema
                            })}
                            
                        >
                            {({ touched, errors, values, setValues, isValid }) => (
                                <Form>
                                    <VStack gap="10px" my="20px">
                                        {
                                            accountExists && (
                                                <>
                                                    <Alert status='info' mt="30px" variant="subtle">
                                                        <AlertIcon />
                                                        {/* <AlertTitle>Your browser is outdated!</AlertTitle> A*/}
                                                        <Text>
                                                            Looks like you already have an account.
                                                            <Button
                                                                as="span"
                                                                variant="link"
                                                                textDecor={'underline'}
                                                                ml={'3px'}
                                                                onClick={openAuthModal}
                                                            >Log in</Button>
                                                        </Text>
                                                    </Alert>

                                                    <AuthModal
                                                    returnToListing
                                                    onClose={closeAuthModal}
                                                    isOpen={showAuthModal}
                                                    
                                                    />
                                                </>    
                                            )
                                        }

                                        {
                                            alert && (
                                                <Alert status={alert.status} mt="30px" variant="solid">
                                                    <AlertIcon />
                                                    {alert.description}
                                                </Alert>   
                                            )
                                        }
                                        <Box>
                                        <Question>
                                            {currentStepData.header}
                                        </Question>
                                        <BottomExplainer>
                                            {currentStepData.explainer}
                                        </BottomExplainer>
                                        </Box>

                                    </VStack>

                                        {
                                            currentStepData.name === 'phone' ? (
                                                <PhoneNumberInput 
                                                    value={phoneInputValue}
                                                    onChange={handlePhoneValueChange}
                                                    placeholder="Enter phone number"
                                                    autoFocus
                                                />
                                            ) :
                                            <Field name={currentStepData.name} component={currentStepData.inputComponent} />
                                        }
                                        {
                                            currentStepData.name === 'email' && (
                                                <TermsAndPrivacyPolicy />
                                            )
                                        }
                                        {
                                            quizType == "callback" && currentStepData.name === 'message' && (
                                                <Box mt="16px">
                                                <OptionsHeader>
                                                    Enter above or simply pick a popular option:
                                                </OptionsHeader>
                                                <OptionButtonList>
                                                    {commonTimings.map((i, index) => (
                                                        <OptionButton key={index}
                                                        onClick={() => {
                                                            callBackRequest({
                                                                ...values,
                                                                message: i
                                                            })}
                                                        }
                                                        >
                                                            {i}
                                                        </OptionButton>
                                                    ))}
                                                </OptionButtonList>
                                                </Box>
                                            )
                                        }
                                        {
                                            currentStepData.name === 'phone' && (
                                                (<Box 
                                                mt="5px"
                                                >
                                                </Box>)
                                                    
                                                // <Alert
                                                //     status="info"
                                                //     bg="#EDF2F7"
                                                //     borderRadius={"16px"}
                                                //     w="100%"
                                                //     fontSize="12px"
                                                //     mt="32px"
                                                // >
                                                //     <LockIcon mr="16px" />
                                                //     <Text>
                                                //         Based on our{" "}
                                                //         <Link href="/terms" passHref>
                                                //         <TermsLink tabIndex={-1} target="_blank">
                                                //             {" "}
                                                //             Terms of Use
                                                //         </TermsLink>
                                                //         </Link>{" "}
                                                //         and{" "}
                                                //         <Link href="/privacy" passHref>
                                                //         <TermsLink tabIndex={-1} target="_blank">
                                                //             {" "}
                                                //             Privacy Policy
                                                //         </TermsLink>
                                                //         </Link>
                                                //         , we might send you critical text alerts when
                                                //         necessary. Message & data rates may apply. Text STOP
                                                //         to opt out.
                                                //     </Text>
                                                // </Alert>
                                            )
                                        }

                            
                                    {
                                        (touched[currentStepData.name] && errors[currentStepData.name]) && (
                                            <Box>
                                                <Text color="tomato" fontSize="12px">
                                                    {errors[currentStepData.name]}
                                                </Text>
                                            </Box>
                                        )
                                    }
                                    {
                                        currentStepData.name === 'message' && touched['message'] && (
                                            <Box>
                                                <Text color="tomato" fontSize="12px">
                                                    {
                                                        values.message.length > 1000 ? 'Message too long' :
                                                        values.message.length < 2 && "Message too short"
                                                    }
                                                </Text>
                                            </Box>
                                        )
                                    }

                                    {/* {
                                        phoneIsInvalid &&
                                        phoneInvalidError && (
                                        <Box>
                                            <Text color="tomato" fontSize="12px">
                                                Phone number is invalid
                                            </Text>
                                        </Box>
                                    )} */}

                                    <Actions>
                                        {
                                            step > 1 &&
                                            (
                                                <Button
                                                    tabIndex={-1}
                                                    variant="outline"
                                                    colorScheme="black"
                                                    isDisabled={
                                                        submitStatus == "loading"
                                                    }
                                                    onClick={prevStep}
                                                    height="56px"
                                                    padding="0 30px"
                                                    mr="20px"
                                                    type="button"
                                                >
                                                    Back
                                                </Button>
                                            )
                                        }
                                        <SubmitButton>
                                            <Button
                                                variant="solid"
                                                width="100%"
                                                isLoading={submitStatus === 'loading'}
                                                loadingText={"Submitting"}
                                                isDisabled={
                                                    errors[currentStepData.name] || 
                                                    (currentStepData.name === 'message' && 
                                                        (values.message.length > 1000 ||
                                                        values.message.length < 2) 
                                                    ) 
                                                    ||
                                                    (
                                                        phoneIsInvalid
                                                    )
                                                }
                                                height="56px"
                                                padding="0 30px"
                                                colorScheme="green"
                                                type="submit"
                                            >
                                                Submit
                                            </Button>
                                        </SubmitButton>
                                    </Actions>
                                </Form>
                            )}
                        </Formik>
                        )
                    }
                </ModalBody>
            </ModalContent>
        </Modal>)
    );
}

export default QuestionsLeadForm

const MessageInput = ({ field, form, ...props }) => {
    return (
        <Input
            _focus={{
                borderColor: "#125f3f",
                boxShadow: "0 0 0 1px #125f3f",
            }}
            // Only do autofocus if this isn't a callback request - callback request has other options they can pick from
            autoFocus={props.quizType !== "callback"}
            type="text"
            id="message"
            name="message"
            placeholder={props.quizType == "question" ? "Type anything" : props.quizType == "callback" ? "Example: Evenings, weekends, anytime" : 
        ""}
            size={"md"}
            {...field}
            {...props}
        />
    )
}

const EmailInput = ({ field, form, ...props }) => {
    return (
        <Input
            _focus={{
                borderColor: "#125f3f",
                boxShadow: "0 0 0 1px #125f3f",
            }}
            autoFocus
            type="email"
            id="email"
            name="email"
            placeholder="julie@example.com"
            size={"md"}
            {...field}
            {...props}
        />
    )
}

const FullNameInput = ({ field, form, ...props }) => {
    return (
        <Input
            autoComplete="name"
            autoFocus
            placeholder="Julie Stevens"
            data-hj-allow
            focusBorderColor="green.500"
            id="fullName"
            name="fullName"
            type="text"
            {...field}
            {...props}
        />
    )
}

const TermsAndPrivacyPolicy = () => (
    <Box mt="15px">
        <BottomExplainer>
            By clicking Submit, you agree to the{" "}
            <Link href="/terms">
            <TermsLink tabIndex={-1} target="_blank">
                {" "}
                Terms of Use
            </TermsLink>
            </Link>{" "}
            and{" "}
            <Link href="/privacy">
            <TermsLink tabIndex={-1} target="_blank">
                {" "}
                Privacy Policy
            </TermsLink>
            </Link>
            .
        </BottomExplainer>
        <Box h="30px" />
    </Box>
)